import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { reportService } from "../services/api";
import { pdfService } from "../services/pdfService";
import { AuthContext } from "../context/AuthContext";
import moment from "moment";
import SignatureCanvas from "react-signature-canvas";
import { useLanguage } from "../context/LanguageContext";

// Material UI components
import {
  Box, Grid, Button, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, AlertTitle, Tabs, Tab, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, LinearProgress, Typography, Paper, IconButton, Stack, Avatar,
  useTheme, FormHelperText
} from "@mui/material";
import { alpha } from "@mui/material/styles";

// Material UI icons
import {
  DescriptionOutlined as DescriptionIcon, PictureAsPdfOutlined as PictureAsPdfIcon, EditOutlined as EditIcon,
  CheckCircleOutline as CheckCircleIcon, WarningAmber as WarningIcon, InfoOutlined as InfoIcon,
  CalendarTodayOutlined as CalendarMonthIcon, AccessTimeOutlined as AccessTimeIcon, PersonOutlined as PersonIcon,
  LocationOnOutlined as LocationOnIcon, SellOutlined as TagIcon, BuildCircleOutlined as BuildIcon,
  EventAvailableOutlined as EventAvailableIcon, ListAltOutlined as ListAltIcon, NotesOutlined as NotesIcon,
  DrawOutlined as DrawIcon, ClearOutlined as ClearIcon, AccountCircleOutlined as AccountCircleIcon,
  CheckOutlined as CheckIcon, CloseOutlined as CloseIcon, ArrowBackOutlined as ArrowBackIcon,
} from "@mui/icons-material";

import PageContainer from "../components/PageContainer";

// TabPanel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`report-tabpanel-${index}`} aria-labelledby={`report-tab-${index}`} {...other}>
      {value === index && <Box sx={{ pt: 2.5 }}>{children}</Box>}
    </div>
  );
}

// Helper function to get category icons
const getCategoryIcon = (category) => {
  switch (category) {
    case 'exterior': return <BuildIcon />;
    case 'interior': return <PersonIcon />;
    case 'engine': return <BuildIcon />;
    case 'cleaning': return <ClearIcon />;
    default: return <ListAltIcon />;
  }
};

// Helper function to format check item names for better display
const formatCheckItemName = (name, t) => {
  // Handle common check item names and provide better formatting
  const checkItemMap = {
    // Exterior checks
    'tracks': t('tracks') || 'Tracks',
    'bucket': t('bucket') || 'Bucket',
    'arm': t('arm') || 'Arm',
    'boom': t('boom') || 'Boom',
    'hydraulicLines': t('hydraulicLines') || 'Hydraulic Lines',
    'lights': t('lights') || 'Lights',
    'mirrors': t('mirrors') || 'Mirrors',
    'bodyDamage': t('bodyDamage') || 'Body Damage',
    'hydraulicLeaks': t('hydraulicLeaks') || 'Hydraulic Leaks',
    'oilLeaks': t('oilLeaks') || 'Oil Leaks',

    // Interior checks
    'seat': t('seat') || 'Seat',
    'controls': t('controls') || 'Controls',
    'dashboard': t('dashboard') || 'Dashboard',
    'seatbelt': t('seatbelt') || 'Seatbelt',
    'windows': t('windows') || 'Windows',
    'airConditioning': t('airConditioning') || 'Air Conditioning',
    'radioFunction': t('radioFunction') || 'Radio Function',
    'emergencyStops': t('emergencyStops') || 'Emergency Stops',

    // Engine checks
    'oilLevel': t('oilLevel') || 'Oil Level',
    'coolantLevel': t('coolantLevel') || 'Coolant Level',
    'fuelLevel': t('fuelLevel') || 'Fuel Level',
    'batteryCondition': t('batteryCondition') || 'Battery Condition',
    'airFilter': t('airFilter') || 'Air Filter',
    'belts': t('belts') || 'Belts',
    'engineNoise': t('engineNoise') || 'Engine Noise',
    'exhaustSmoke': t('exhaustSmoke') || 'Exhaust Smoke',

    // Cleaning checks
    'exteriorCleaning': t('exteriorCleaning') || 'Exterior Cleaning',
    'interiorCleaning': t('interiorCleaning') || 'Interior Cleaning',
    'windowsCleaning': t('windowsCleaning') || 'Windows Cleaning',
    'tracksCleaning': t('tracksCleaning') || 'Tracks Cleaning',
  };

  // Return mapped name or format the original name
  return checkItemMap[name] ||
    t(name) ||
    name.replace(/([A-Z])/g, ' $1') // Add spaces before capital letters
      .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
      .trim();
};

// Category Summary Card component
const CategorySummaryCard = ({ category, checks, theme, t }) => {
  const totalChecks = checks.length;
  const completedChecks = checks.filter(check => check.completed).length;
  const completionRate = totalChecks > 0 ? (completedChecks / totalChecks) * 100 : 0;
  const hasIssues = completedChecks < totalChecks;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        height: '100%',
        bgcolor: hasIssues ? alpha(theme.palette.warning.main, 0.05) : alpha(theme.palette.success.main, 0.05),
        borderColor: hasIssues ? alpha(theme.palette.warning.main, 0.3) : alpha(theme.palette.success.main, 0.3),
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4]
        }
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <Avatar sx={{
          width: 32,
          height: 32,
          bgcolor: hasIssues ? alpha(theme.palette.warning.main, 0.15) : alpha(theme.palette.success.main, 0.15),
          color: hasIssues ? 'warning.dark' : 'success.dark'
        }}>
          {React.cloneElement(getCategoryIcon(category), { fontSize: 'small' })}
        </Avatar>
        <Typography variant="h6" fontWeight="medium" sx={{ textTransform: 'capitalize' }}>
          {t(`${category}Checks`) || t(category) || category}
        </Typography>
      </Stack>

      <Box sx={{ mb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
          <Typography variant="body2" color="text.secondary">
            {completedChecks}/{totalChecks} {t("completed")}
          </Typography>
          <Typography variant="body2" fontWeight="medium" color={hasIssues ? 'warning.dark' : 'success.dark'}>
            {completionRate.toFixed(0)}%
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={completionRate}
          color={hasIssues ? "warning" : "success"}
          sx={{ height: 6, borderRadius: 3 }}
        />
      </Box>

      {hasIssues && (
        <Chip
          icon={<WarningIcon />}
          label={`${totalChecks - completedChecks} ${t("issues")}`}
          color="warning"
          size="small"
          variant="outlined"
        />
      )}
    </Paper>
  );
};

// StatCard for key details - similar to one from ExcavatorDetail for consistency
const DetailItemCard = ({ icon, label, value, chip, accent, sx }) => {
  const theme = useTheme();
  return (
    <Paper variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2, bgcolor: accent ? alpha(theme.palette.primary.main, 0.03) : 'transparent', borderColor: accent ? alpha(theme.palette.primary.main, 0.3) : 'divider', ...sx }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, color: accent ? 'primary.main' : 'text.secondary' }}>
        {icon && React.cloneElement(icon, { fontSize: 'small' })}
        <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 'medium' }}>{label}</Typography>
      </Stack>
      {chip || <Typography variant="body1" fontWeight="medium" color="text.primary">{value || "-"}</Typography>}
    </Paper>
  );
};

const ReportDetail = () => {
  const { t } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const { isManager, currentUser } = useContext(AuthContext);
  const theme = useTheme();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [showSignModal, setShowSignModal] = useState(false);
  const [signingReport, setSigningReport] = useState(false);
  const [signatureError, setSignatureError] = useState('');
  const signatureCanvasRef = useRef(null);
  const [useProfileSig, setUseProfileSig] = useState(true);

  const fetchReport = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const response = await reportService.getById(id);
      setReport(response.data);
      // Pre-fill signature option and data
      if (currentUser?.signature) {
        setUseProfileSig(true);
        // Auto-set signature data for seamless approval
      } else {
        setUseProfileSig(false);
      }
    } catch (err) {
      console.error("Error fetching report:", err);
      setError(t("failedToLoadReport") + (err.message ? `: ${err.message}` : ''));
    } finally {
      setLoading(false);
    }
  }, [id, t, currentUser?.signature]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleDownloadPdf = async () => {
    setPdfLoading(true); setError(null);
    try {
      await pdfService.downloadReportPDF(id);
    } catch (err) { console.error("Error downloading PDF:", err); setError(t("errorDownloadingPDF")); }
    finally { setPdfLoading(false); }
  };

  const handleSignReport = async () => {
    let signatureDataURL = null;
    setSignatureError('');

    if (useProfileSig && currentUser?.signature) {
      signatureDataURL = currentUser.signature;
    } else {
      if (!signatureCanvasRef.current || signatureCanvasRef.current.isEmpty()) {
        setSignatureError(t("signatureRequired")); return;
      }
      signatureDataURL = signatureCanvasRef.current.getTrimmedCanvas().toDataURL("image/png");
    }

    if (!signatureDataURL) { // Should not happen if logic above is correct
      setSignatureError(t("signatureRequired")); return;
    }

    setSigningReport(true); setError(null);
    try {
      const response = await reportService.approve(id, {
        managerSignature: signatureDataURL,
        reviewedBy: currentUser?.name, // Manager's name
        status: "signed", // Update status
        managerId: currentUser?._id,
      });
      setReport(response.data.report || response.data); // Ensure correct data structure
      setShowSignModal(false);
      // Optionally show success snackbar
    } catch (err) {
      console.error("Error signing report:", err);
      setError(t("failedToSignReport") + (err.response?.data?.message ? `: ${err.response.data.message}` : ''));
    } finally {
      setSigningReport(false);
    }
  };

  const clearSignature = () => { if (signatureCanvasRef.current) signatureCanvasRef.current.clear(); setSignatureError(''); };

  // Helper function to check overall completion status
  const getCompletionStatus = () => {
    if (totalCheckItems === 0) return { status: 'unknown', icon: <InfoIcon />, color: 'default' };
    if (completedCheckItems === totalCheckItems) return { status: 'complete', icon: <CheckCircleIcon />, color: 'success' };
    if (completedCheckItems === 0) return { status: 'incomplete', icon: <WarningIcon />, color: 'error' };
    return { status: 'partial', icon: <WarningIcon />, color: 'warning' };
  };

  const getStatusChipProps = (status) => {
    switch (status?.toLowerCase()) {
      case "signed": case "completed": case "approved": return { color: "success", icon: <CheckCircleIcon />, label: t('signed') };
      case "unsigned": case "pending": return { color: "warning", icon: <WarningIcon />, label: t('unsigned') };
      case "scheduled": return { color: "info", icon: <CalendarMonthIcon />, label: t('scheduled') };
      default: return { color: "default", icon: <InfoIcon />, label: t(status || "unknownStatus") };
    }
  };

  const renderCheckStatusIcon = (completed) => (
    <Avatar sx={{ width: 28, height: 28, bgcolor: completed ? alpha(theme.palette.success.main, 0.15) : alpha(theme.palette.error.main, 0.15), color: completed ? "success.dark" : "error.dark" }}>
      {completed ? <CheckIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
    </Avatar>
  );

  if (loading) {
    return <PageContainer title={t("loading")}><Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box></PageContainer>;
  }
  if (error && !report) {
    return <PageContainer title={t("error")}><Alert severity="error" sx={{ m: 2 }}><AlertTitle>{t("error")}</AlertTitle>{error}</Alert><Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ m: 2 }}>{t("back")}</Button></PageContainer>;
  }
  if (!report) {
    return <PageContainer title={t("notFound")}><Alert severity="warning" sx={{ m: 2 }}>{t("reportNotFound")}</Alert><Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ m: 2 }}>{t("back")}</Button></PageContainer>;
  }

  const checklistCategories = ["exterior", "interior", "engine", "cleaning"];
  const allChecks = [];
  let totalCheckItems = 0, completedCheckItems = 0;

  if (report.checks) {
    checklistCategories.forEach(category => {
      if (Array.isArray(report.checks[category])) {
        // Handle array structure from CreateReport form
        report.checks[category].forEach(check => {
          if (check && check.name && check.name.trim() !== '') { // Only process items with actual names
            const isCompleted = ["ok", "passed", "completed"].includes(check.status?.toLowerCase());
            allChecks.push({
              name: check.name,
              completed: isCompleted,
              category,
              notes: check.notes
            });
            totalCheckItems++;
            if (isCompleted) completedCheckItems++;
          }
        });
      } else if (report.checks[category] && typeof report.checks[category] === 'object') {
        // Handle flat object structure (legacy or alternative format)
        Object.entries(report.checks[category]).forEach(([key, value]) => {
          if (key && key !== '' && !key.match(/^\d+$/) && key.length > 1) { // Skip empty keys, numeric indices, and single chars
            const isCompleted = typeof value === 'boolean' ? value : (value?.status === 'completed' || value === true);
            allChecks.push({ name: key, completed: isCompleted, category });
            totalCheckItems++;
            if (isCompleted) completedCheckItems++;
          }
        });
      }
    });
  }
  const checklistCompletionPercentage = totalCheckItems > 0 ? (completedCheckItems / totalCheckItems) * 100 : 0;

  const { icon: statusIcon, color: statusColor, label: statusLabel } = getStatusChipProps(report.status);
  const completionStatus = getCompletionStatus();

  return (
    <PageContainer title={`${t("report")} #${report._id.slice(-6)}`}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {/* Header section */}
      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: theme.shape.borderRadius * 1.5, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.background.paper, 0.5)} 100%)`, border: `1px solid ${theme.palette.divider}` }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md>
            <Stack direction="row" alignItems="center" spacing={2} mb={1}>
              <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.15), color: "primary.main", width: 56, height: 56 }}>
                <DescriptionIcon fontSize="large" />
              </Avatar>
              <div>
                <Typography variant="h5" component="h1" fontWeight="bold">
                  {report.type ? t(report.type) : t("maintenanceReport")}
                  {report.excavator ? ` - ${report.excavator.manufacturer} ${report.excavator.model}` : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("reportId")}: #{report._id}
                </Typography>
              </div>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Chip icon={statusIcon} label={statusLabel} color={statusColor} size="medium" />
              <Chip icon={<CalendarMonthIcon />} label={moment(report.reportDate || report.createdAt).format("LL")} variant="outlined" size="small" />
              <Chip icon={<AccessTimeIcon />} label={`${report.operatingHours || '-'} ${t("hours")}`} variant="outlined" size="small" />
              {totalCheckItems > 0 && (
                <Chip
                  icon={completionStatus.icon}
                  label={`${completedCheckItems}/${totalCheckItems} ${t("checked")} (${checklistCompletionPercentage.toFixed(0)}%)`}
                  color={completionStatus.color}
                  variant="outlined"
                  size="small"
                />
              )}
            </Stack>
          </Grid>
          <Grid item xs={12} md="auto">
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="stretch">
              <Button variant="outlined" color="secondary" startIcon={<ArrowBackIcon />} onClick={() => navigate("/reports")}>{t("backToList")}</Button>
              {isManager() && report.status !== "signed" && (
                <Button variant="contained" color="primary" startIcon={<EditIcon />} onClick={() => setShowSignModal(true)}>{t("approveAndSign")}</Button>
              )}
              <Button variant="contained" color="primary" startIcon={<PictureAsPdfIcon />} onClick={handleDownloadPdf} disabled={pdfLoading}>
                {pdfLoading ? <CircularProgress size={20} color="inherit" /> : t("downloadPDF")}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs for content */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 0 }}>
        <Tabs value={activeTab} onChange={(e, newVal) => setActiveTab(newVal)} indicatorColor="primary" textColor="primary" variant="scrollable" scrollButtons="auto">
          <Tab label={t("overview")} icon={<InfoIcon />} iconPosition="start" id="tab-overview" />
          <Tab label={t("checklistDetails")} icon={<ListAltIcon />} iconPosition="start" id="tab-checklist" />
          <Tab label={t("notesAndSignatures")} icon={<NotesIcon />} iconPosition="start" id="tab-notes" />
        </Tabs>
      </Box>

      <Paper variant="outlined" sx={{ borderTopLeftRadius: 0, borderTopRightRadius: 0, borderColor: theme.palette.divider, mb: 3 }}>
        <TabPanel value={activeTab} index={0}> {/* Overview */}
          {/* Key Information Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}><DetailItemCard icon={<BuildIcon />} label={t("excavator")} value={report.excavator ? `${report.excavator.manufacturer} ${report.excavator.model}` : t('notSpecified')} accent /></Grid>
            <Grid item xs={12} md={4}><DetailItemCard icon={<TagIcon />} label={t("serialNumber")} value={report.excavator?.serialNumber || t('notSpecified')} /></Grid>
            <Grid item xs={12} md={4}><DetailItemCard icon={<PersonIcon />} label={t("technician")} value={report.technician || report.operator?.name || t('notSpecified')} /></Grid>
            <Grid item xs={12} md={4}><DetailItemCard icon={<CalendarMonthIcon />} label={t("reportDate")} value={moment(report.reportDate || report.createdAt).format("LL")} /></Grid>
            <Grid item xs={12} md={4}><DetailItemCard icon={<AccessTimeIcon />} label={t("operatingHoursAtReport")} value={`${report.operatingHours || '-'} ${t("hrs")}`} /></Grid>
            <Grid item xs={12} md={4}><DetailItemCard icon={<LocationOnIcon />} label={t("location")} value={report.excavator?.location || t('notSpecified')} /></Grid>
            {report.nextMaintenanceHours && <Grid item xs={12} md={4}><DetailItemCard icon={<EventAvailableIcon />} label={t("nextMaintenanceAt")} value={`${report.nextMaintenanceHours} ${t("hrs")}`} /></Grid>}
            {report.nextUVVDate && <Grid item xs={12} md={4}><DetailItemCard icon={<EventAvailableIcon />} label={t("nextUVVInspection")} value={moment(report.nextUVVDate).format("LL")} /></Grid>}
          </Grid>

          {/* Checklist Category Summaries */}
          {allChecks.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <ListAltIcon color="primary" />
                {t("checklistSummary")}
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {checklistCategories.map(category => {
                  const categoryChecks = allChecks.filter(check => check.category === category);
                  if (categoryChecks.length === 0) return null;
                  return (
                    <Grid item xs={12} sm={6} md={3} key={category}>
                      <CategorySummaryCard
                        category={category}
                        checks={categoryChecks}
                        theme={theme}
                        t={t}
                      />
                    </Grid>
                  );
                })}
              </Grid>

              {/* Overall Progress */}
              <Paper variant="outlined" sx={{ p: 3, bgcolor: alpha(theme.palette.background.default, 0.3) }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon color="primary" />
                  {t("overallProgress")}
                </Typography>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h4" fontWeight="bold" color={checklistCompletionPercentage === 100 ? "success.main" : "warning.main"}>
                    {checklistCompletionPercentage.toFixed(0)}%
                  </Typography>
                  <Stack alignItems="flex-end">
                    <Typography variant="body1" fontWeight="medium">
                      {completedCheckItems} {t("of")} {totalCheckItems} {t("itemsChecked")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {totalCheckItems - completedCheckItems} {t("remaining")}
                    </Typography>
                  </Stack>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={checklistCompletionPercentage}
                  color={checklistCompletionPercentage === 100 ? "success" : "warning"}
                  sx={{ height: 12, borderRadius: 6 }}
                />
              </Paper>
            </>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={1}> {/* Checklist */}
          {allChecks.length > 0 ? (<>
            {/* Quick Stats */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="h6" fontWeight="medium">{t("checklistDetails")}</Typography>
                    <Chip label={`${checklistCompletionPercentage.toFixed(0)}% ${t("complete")}`} color={checklistCompletionPercentage === 100 ? "success" : "warning"} />
                  </Stack>
                  <LinearProgress variant="determinate" value={checklistCompletionPercentage} color={checklistCompletionPercentage === 100 ? "success" : "warning"} sx={{ height: 8, borderRadius: 4 }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon fontSize="small" color="success" />
                      {completedCheckItems} {t("completed")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WarningIcon fontSize="small" color="warning" />
                      {totalCheckItems - completedCheckItems} {t("pending")}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>

            {/* Categorized Checklist */}
            {checklistCategories.map(category => {
              const categoryChecks = allChecks.filter(item => item.category === category);
              if (categoryChecks.length === 0) return null;

              const categoryCompleted = categoryChecks.filter(item => item.completed).length;
              const categoryTotal = categoryChecks.length;
              const categoryPercentage = (categoryCompleted / categoryTotal) * 100;

              return (
                <Paper key={category} variant="outlined" sx={{ mb: 2 }}>
                  <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.03), borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, textTransform: 'capitalize' }}>
                        {React.cloneElement(getCategoryIcon(category), { fontSize: 'small', color: 'primary' })}
                        {t(`${category}Checks`) || t(category) || category} {t("checks")}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          {categoryCompleted}/{categoryTotal}
                        </Typography>
                        <Chip
                          label={`${categoryPercentage.toFixed(0)}%`}
                          color={categoryPercentage === 100 ? "success" : "warning"}
                          size="small"
                        />
                      </Stack>
                    </Stack>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: alpha(theme.palette.background.default, 0.5) }}>
                          <TableCell sx={{ width: 60 }} align="center">{t("status")}</TableCell>
                          <TableCell>{t("item")}</TableCell>
                          <TableCell>{t("notes")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {categoryChecks.map((item, index) => (
                          <TableRow
                            key={`${category}-${index}`}
                            hover
                            sx={{
                              bgcolor: item.completed
                                ? alpha(theme.palette.success.main, 0.05)
                                : alpha(theme.palette.error.main, 0.05),
                              '&:hover': {
                                bgcolor: item.completed
                                  ? alpha(theme.palette.success.main, 0.1)
                                  : alpha(theme.palette.error.main, 0.1)
                              }
                            }}
                          >
                            <TableCell align="center">{renderCheckStatusIcon(item.completed)}</TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {formatCheckItemName(item.name, t)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {item.notes || '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              );
            })}
          </>) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <ListAltIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t("noChecklistDataAvailable")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("checklistDataWillAppearHere")}
              </Typography>
            </Paper>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={2}> {/* Notes & Signatures */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={report.operatorSignature || report.managerSignature ? 6 : 12}>
              <Typography variant="h6" gutterBottom>{t("notesAndObservations")}</Typography>
              {report.notes ? <Paper variant="outlined" sx={{ p: 2, whiteSpace: "pre-wrap", minHeight: 150, bgcolor: alpha(theme.palette.info.main, 0.03) }}><Typography variant="body2">{report.notes}</Typography></Paper> : <Typography color="text.secondary">{t("noNotesRecorded")}</Typography>}
            </Grid>
            {(report.operatorSignature || report.managerSignature) && (
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>{t("signatures")}</Typography>
                {report.operatorSignature && (<Box mb={2}><Typography variant="subtitle2" color="text.secondary">{t("technicianSignature")} ({report.technician || report.operator?.name}):</Typography><Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}><img src={report.operatorSignature} alt={t("technicianSignature")} style={{ maxWidth: '100%', maxHeight: 100, border: `1px solid ${theme.palette.divider}` }} /></Paper></Box>)}
                {report.managerSignature && (<Box><Typography variant="subtitle2" color="text.secondary">{t("managerSignature")} ({report.managerName || t("manager")}):</Typography><Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}><img src={report.managerSignature} alt={t("managerSignature")} style={{ maxWidth: '100%', maxHeight: 100, border: `1px solid ${theme.palette.divider}` }} /></Paper></Box>)}
              </Grid>
            )}
          </Grid>
        </TabPanel>
      </Paper>

      {/* Signature Dialog */}
      <Dialog open={showSignModal} onClose={() => setShowSignModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("signReportAsManager")}</DialogTitle>
        <DialogContent>
          {signatureError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSignatureError('')}>{signatureError}</Alert>}
          <Typography variant="body2" sx={{ mb: 1.5 }}>{t("pleaseProvideSignatureToApprove")}</Typography>
          <TextField label={t("signingAs")} value={currentUser?.name || ""} fullWidth InputProps={{ readOnly: true }} variant="filled" size="small" sx={{ mb: 1.5 }} />

          {currentUser?.signature && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <Button
                size="small"
                variant={useProfileSig ? "contained" : "outlined"}
                onClick={() => {
                  setUseProfileSig(true);
                  if (signatureCanvasRef.current) signatureCanvasRef.current.clear();
                  setSignatureError('');
                }}
                startIcon={<AccountCircleIcon />}
              >
                {t("useProfileSignature")}
              </Button>
              <Button
                size="small"
                variant={!useProfileSig ? "contained" : "outlined"}
                onClick={() => {
                  setUseProfileSig(false);
                  setSignatureError('');
                }}
                startIcon={<EditIcon />}
              >
                {t("drawNewSignature")}
              </Button>
            </Stack>
          )}

          {useProfileSig && currentUser?.signature ? (
            <Paper variant="outlined" sx={{ p: 1, textAlign: 'center', height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><img src={currentUser.signature} alt={t("profileSignatureAlt")} style={{ maxWidth: "100%", maxHeight: "140px", border: `1px solid ${theme.palette.divider}` }} /></Paper>
          ) : (
            <Paper variant="outlined" sx={{ borderColor: signatureError && !useProfileSig ? "error.main" : "divider", height: 150, position: "relative" }}>
              <SignatureCanvas ref={signatureCanvasRef} penColor={theme.palette.text.primary} canvasProps={{ style: { width: "100%", height: "100%" } }} onBegin={() => setSignatureError('')} />
              <IconButton onClick={clearSignature} size="small" sx={{ position: "absolute", top: 4, right: 4 }}><ClearIcon fontSize="small" /></IconButton>
            </Paper>
          )}
          {signatureError && !useProfileSig && <FormHelperText error sx={{ mt: 0.5 }}>{signatureError}</FormHelperText>}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
          <Button onClick={() => { setShowSignModal(false); setSignatureError(''); }} color="secondary">{t("cancel")}</Button>
          <Button variant="contained" onClick={handleSignReport} disabled={signingReport} startIcon={signingReport ? <CircularProgress size={18} color="inherit" /> : <DrawIcon />}>
            {signingReport ? t("signing") : t("signAndApprove")}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default ReportDetail;
